<template>
  <div class="pos-order-container row g-0 m-0">

    <div class="col-12 col-lg-8 d-flex flex-column h-100 p-0 pe-lg-3">

      <!-- Search + category pill bar (scroll-spy target) -->
      <div class="bg-white rounded-3 shadow-sm border p-2 p-lg-3 mb-3 flex-shrink-0">
        <!-- ค้นหาชื่ออาหาร — กรองสินค้าทุกหมวดพร้อมกัน หมวดที่ไม่เหลือรายการจะหายไปเอง
             (visibleCategories ตัดหมวดว่างอยู่แล้ว) จึงไม่ต้องมีโหมด "ผลการค้นหา" แยก -->
        <div class="menu-search mb-2">
          <i class="fa-solid fa-magnifying-glass menu-search__icon" />
          <input
            v-model="search"
            type="text"
            class="menu-search__input"
            :placeholder="$t('Search menu')"
            :aria-label="$t('Search menu')"
          >
          <button
            v-if="search"
            type="button"
            class="menu-search__clear"
            :aria-label="$t('Clear')"
            @click="search = ''"
          >
            <i class="fa-solid fa-xmark" />
          </button>
        </div>

        <div v-if="pageLoading" class="d-flex overflow-x-auto hide-scrollbar gap-2">
          <span class="placeholder-glow flex-shrink-0" v-for="i in 5" :key="'cat-ph-'+i">
            <span class="placeholder rounded-pill" style="width: 84px; height: 34px;">&nbsp;</span>
          </span>
        </div>
        <div v-else ref="pillBar" class="d-flex overflow-x-auto hide-scrollbar gap-2">
          <button
            v-for="cat in visibleCategories"
            :key="cat.id"
            :ref="el => setPillRef(cat.id, el)"
            type="button"
            class="cat-pill flex-shrink-0"
            :class="{ active: activeCategoryId === cat.id }"
            @click="scrollToCategory(cat.id, $event)"
          >
            <i v-if="cat.id === RECOMMENDED_ID" class="fa-solid fa-star text-warning" />
            <span>{{ cat.id === RECOMMENDED_ID ? $t('Recommended') : $ln(cat) }}</span>
            <span class="cat-pill-count">{{ groupedProducts[cat.name]?.length || 0 }}</span>
          </button>
        </div>
      </div>

      <!-- Scrollable area: category sections -->
      <div
        ref="scrollContainer"
        class="menu-scroll hide-scrollbar overflow-y-auto overflow-x-hidden flex-grow-1"
        @scroll="onScroll"
      >

        <!-- Placeholder Loading — square poster shape matching the cards. -->
        <div v-if="loading" class="g-2 row">
          <div class="col-6 col-md-4 col-xl-3" v-for="i in 8" :key="'ph-'+i">
            <div class="skeleton-shimmer skeleton-poster shadow-sm"></div>
          </div>
        </div>

        <!-- Empty — ระหว่างค้นหาให้ทางออกในตัว (บอกให้ลองคำอื่น + ปุ่มล้างคำค้น) -->
        <div v-else-if="visibleCategories.length === 0" class="text-center py-5 text-muted">
          <i class="fa-solid fa-bowl-rice fa-3x mb-3 opacity-50"></i>
          <p class="fw-bold mb-1">{{ $t('No products found') }}</p>
          <template v-if="search">
            <p class="small mb-3">{{ $t('Try a different search term') }}</p>
            <button type="button" class="btn btn-sm btn-theme text-white rounded-pill px-4" @click="search = ''">
              <i class="fa-solid fa-xmark me-1" />{{ $t('Clear') }}
            </button>
          </template>
        </div>

        <!-- Category sections -->
        <template v-else v-for="cat in visibleCategories" :key="cat.id">
          <section
            :ref="el => setSectionRef(cat.id, el)"
            :data-cat-id="cat.id"
            class="menu-section mb-4"
          >
            <div class="section-head d-flex align-items-center mb-2">
              <span class="section-accent" />
              <i v-if="cat.id === RECOMMENDED_ID" class="fa-solid fa-star me-2 text-warning" />
              <h6 class="section-title fw-bold text-dark mb-0">
                {{ cat.id === RECOMMENDED_ID ? $t('Recommended') : $ln(cat) }}
              </h6>
              <span class="section-count ms-2">{{ groupedProducts[cat.name].length }}</span>
            </div>

            <!-- เมนูแนะนำ = การ์ดโปสเตอร์ (รูปใหญ่ดึงสายตา) ชื่อ/รายละเอียดอยู่บนพื้นขาวใต้รูป
                 หมวดอื่น = การ์ดแนวนอน สแกนชื่อ+รายละเอียด+ราคาได้เร็วกว่าตอนรายการยาว -->
            <div v-if="cat.id === RECOMMENDED_ID" class="row g-2">
              <div
                v-for="product in groupedProducts[cat.name]"
                :key="product.id"
                class="col-6 col-md-4 col-xl-3"
              >
                <div
                  class="product-card shadow-sm h-100"
                  :class="{ 'product-card--disabled': soldOut(product) }"
                  @click="handleProductClick(product)"
                >
                  <div class="product-media">
                    <img
                      v-if="product.image_url || product.thumbnail_url"
                      :src="$storeIndex.menuImageUrl(product)"
                      class="product-image"
                      loading="lazy"
                      decoding="async"
                      @error="$storeIndex.onImageError"
                    >
                    <div v-else class="product-placeholder">
                      <i class="fa-solid fa-utensils" />
                    </div>

                    <!-- Top-left badge stack: price (with reco star) + options. -->
                    <div class="overlay-badges">
                      <span v-if="Number(product.price) > 0" class="overlay-price" :class="{ 'overlay-price--promo': product.promotion_price != null }">
                        <i v-if="product.is_recommend && !soldOut(product)" class="fa-solid fa-star text-warning me-1" />
                        <template v-if="product.promotion_price != null">
                          {{ $storeIndex.formatThaiCurrency(product.promotion_price) }}
                          <span class="text-decoration-line-through fw-normal ms-1" style="font-size: 0.72em; opacity: 0.7;">
                            {{ $storeIndex.formatThaiCurrency(product.price) }}
                          </span>
                        </template>
                        <template v-else>
                          {{ $storeIndex.formatThaiCurrency(product.price) }}
                          <span v-if="Number(product.compare_at_price) > Number(product.price)" class="text-decoration-line-through fw-normal ms-1" style="font-size: 0.72em; opacity: 0.7;">
                            {{ $storeIndex.formatThaiCurrency(product.compare_at_price) }}
                          </span>
                        </template>
                      </span>
                      <span
                        v-else-if="product.is_recommend && !soldOut(product)"
                        class="overlay-price overlay-price--reco"
                      >
                        <i class="fa-solid fa-star me-1" />{{ $t('Recommended') }}
                      </span>
                      <span v-if="product.has_variants || product.has_modifiers" class="overlay-options">
                        <i class="fa-solid fa-sliders me-1" />{{ $t('Options') }}
                      </span>
                    </div>

                    <span v-if="soldOut(product)" class="overlay-stock">{{ $t('Sold out') }}</span>
                  </div>

                  <!-- พื้นขาว: ชื่อ + รายละเอียดสินค้า + ปุ่มเพิ่ม (ปุ่มไม่มี handler
                       ของตัวเอง — คลิกจะ bubble ขึ้นไปที่การ์ด) -->
                  <div class="product-body">
                    <div class="product-body__text">
                      <h6 class="product-name mb-0">{{ $ln(product) }}</h6>
                      <p v-if="productDesc(product)" class="product-desc mb-0">{{ productDesc(product) }}</p>
                    </div>
                    <button
                      class="card-add-btn flex-shrink-0"
                      :class="{ 'card-add-btn--options': hasOptions(product) }"
                      :disabled="soldOut(product)"
                      :aria-label="hasOptions(product) ? $t('Options') : $t('Add to Cart')"
                    >
                      <i :class="hasOptions(product) ? 'fa-solid fa-sliders' : 'fa-solid fa-plus'" />
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div v-else class="row g-2">
              <div
                v-for="product in groupedProducts[cat.name]"
                :key="product.id"
                class="col-12 col-md-6 col-xxl-4"
              >
                <div
                  class="product-row"
                  :class="{ 'product-row--disabled': soldOut(product) }"
                  @click="handleProductClick(product)"
                >
                  <div class="product-row__media">
                    <img
                      v-if="product.image_url || product.thumbnail_url"
                      :src="$storeIndex.menuImageUrl(product)"
                      class="product-row__image"
                      loading="lazy"
                      decoding="async"
                      @error="$storeIndex.onImageError"
                    >
                    <div v-else class="product-row__placeholder">
                      <i class="fa-solid fa-utensils" />
                    </div>
                    <span v-if="soldOut(product)" class="product-row__stock">{{ $t('Sold out') }}</span>
                  </div>

                  <div class="product-row__body">
                    <h6 class="product-name mb-0">
                      <i v-if="product.is_recommend && !soldOut(product)" class="fa-solid fa-star text-warning me-1" />{{ $ln(product) }}
                    </h6>
                    <p v-if="productDesc(product)" class="product-desc mb-0">{{ productDesc(product) }}</p>

                    <div class="product-row__foot">
                      <span v-if="Number(product.price) > 0" class="row-price" :class="{ 'row-price--promo': product.promotion_price != null }">
                        <template v-if="product.promotion_price != null">
                          {{ $storeIndex.formatThaiCurrency(product.promotion_price) }}
                          <span class="row-price__old text-decoration-line-through">
                            {{ $storeIndex.formatThaiCurrency(product.price) }}
                          </span>
                        </template>
                        <template v-else>
                          {{ $storeIndex.formatThaiCurrency(product.price) }}
                          <span v-if="Number(product.compare_at_price) > Number(product.price)" class="row-price__old text-decoration-line-through">
                            {{ $storeIndex.formatThaiCurrency(product.compare_at_price) }}
                          </span>
                        </template>
                      </span>
                      <button
                        class="card-add-btn flex-shrink-0"
                        :class="{ 'card-add-btn--options': hasOptions(product) }"
                        :disabled="soldOut(product)"
                        :aria-label="hasOptions(product) ? $t('Options') : $t('Add to Cart')"
                      >
                        <i :class="hasOptions(product) ? 'fa-solid fa-sliders' : 'fa-solid fa-plus'" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </template>
      </div>

    </div>

    <!-- Cart Panel -->
    <cart-panel
      ref="cartPanel"
      @editCartItem="handleEditCartItem"
      @editSentItem="handleEditSentItem"
    />

    <div class="d-lg-none position-fixed bottom-0 start-0 w-100 bg-white border-top shadow-lg z-3 pb-safe">
      <button class="btn btn-theme w-100 py-3 fw-bold fs-5 rounded-0 border-0" data-bs-toggle="offcanvas"
        data-bs-target="#cartOffcanvas">
        <div class="d-flex justify-content-between align-items-center px-3">
          <span><i class="fa-solid fa-cart-shopping me-2"></i>{{ $t('View Order') }} ({{ this.$refs.cartPanel?.cartTotalItems }})</span>
          <span>{{ $storeIndex.formatThaiCurrency(this.$refs.cartPanel?.cartTotalPrice) }}</span>
        </div>
      </button>
    </div>

    <!-- Offcanvas Product Options -->
    <offcanvas-product-options
      v-if="offcanvasProduct.open"
      :id="offcanvasProduct.id"
      :edit-data="offcanvasProduct.editData"
      :edit-index="offcanvasProduct.editIndex"
      :platform-prices="platformPrices"
      @close="offcanvasProduct.open = false; offcanvasProduct.editData = null; offcanvasProduct.editIndex = -1; offcanvasProduct.editSentItem = null"
      @addToCart="onAddToCart"
      @updateCartItem="onUpdateCartItem"
    />

  </div>
</template>

<script>
import { Sessions, POSDeliveryPlatforms } from '@/services/endpoint/pos.service';
import { useShopStore } from '@/stores/shop.store';
import { isServiceSessionInvalid } from '@/services/endpoint/services.service';
import OffcanvasProductOptions from './OffcanvasProductOptions.vue';
import CartPanel from './CartPanel.vue';
import { dismissOffcanvas } from '@/utils/offcanvasHelper';
import { pickLocalized } from '@/utils/i18nName';
import { stripHtml } from '@/utils/helpers';

const RECOMMENDED_ID = '__recommended';
const RECOMMENDED_NAME = '__recommended_section__';

export default {
  name: "PosOrder",
  components: {
    OffcanvasProductOptions,
    CartPanel
  },
  data() {
    return {
      RECOMMENDED_ID,
      pageLoading: true,
      loading: false,
      products: [],
      categories: [],
      search: '',
      activeCategoryId: null,
      sectionEls: {},
      pillEls: {},
      _suppressSpyUntil: 0,
      _rafPending: false,
      // Guards the session-invalid redirect so it only navigates once.
      _redirecting: false,
      offcanvasProduct: {
        open: false,
        loading: false,
        id: null,
        editData: null,
        editIndex: -1,
        editSentItem: null
      },
      // ราคา platform ของ session delivery (external) — overlay ทับราคาหน้าร้าน
      // ให้ราคาที่พนักงานเห็น = ราคาที่ BE จะบันทึกจริง (docs/delivery-platform-pricing.md)
      platformInfo: null,
      platformPrices: null,
    };
  },
  computed: {
    // ค้นหาชื่ออาหาร: กรองที่ต้นทางแล้วปล่อยให้ grouping/scroll-spy ทำงานต่อตามปกติ
    // (หมวดที่ไม่เหลือรายการถูกตัดใน visibleCategories อยู่แล้ว) — ค้นชื่อไทย/อังกฤษ
    // และรหัสสินค้า เพราะพนักงานหน้าร้านมักจำเป็นรหัส
    filteredProducts() {
      const keyword = this.search.trim().toLowerCase();
      if (!keyword) return this.products;
      return this.products.filter(p =>
        (p.name || '').toLowerCase().includes(keyword)
        || (p.name_en || '').toLowerCase().includes(keyword)
        || (p.code || '').toLowerCase().includes(keyword)
      );
    },
    recommendedProducts() {
      return this.filteredProducts.filter(p => p.is_recommend);
    },
    groupedProducts() {
      const groups = {};
      if (this.recommendedProducts.length) {
        groups[RECOMMENDED_NAME] = this.recommendedProducts;
      }
      for (const cat of this.categories) groups[cat.name] = [];
      for (const p of this.filteredProducts) {
        if (groups[p.category]) groups[p.category].push(p);
        else if (p.category) groups[p.category] = [p];
      }
      return groups;
    },
    visibleCategories() {
      const known = new Set(this.categories.map(c => c.name));
      const extras = Object.keys(this.groupedProducts)
        .filter(name => name !== RECOMMENDED_NAME && !known.has(name))
        .map(name => ({ id: 'x-' + name, name }));
      const real = [...this.categories, ...extras]
        .filter(c => (this.groupedProducts[c.name] || []).length > 0);
      const list = [];
      if (this.recommendedProducts.length) {
        list.push({ id: RECOMMENDED_ID, name: RECOMMENDED_NAME });
      }
      list.push(...real);
      return list;
    },
  },
  watch: {
    visibleCategories: {
      handler(next) {
        if (!next.length) {
          this.activeCategoryId = null;
          return;
        }
        if (!next.find(c => c.id === this.activeCategoryId)) {
          this.activeCategoryId = next[0].id;
        }
        this.$nextTick(() => this.updateActiveFromScroll());
      },
    },
    activeCategoryId(id) {
      this.scrollActivePillIntoView(id);
    },
    // ผลลัพธ์เปลี่ยนชุด → ดีดกลับหัวลิสต์ ไม่งั้นจะค้างอยู่ตำแหน่ง scroll เดิมของผลชุดก่อน
    // (ซึ่งมักเลยรายการที่เหลืออยู่ไปแล้ว ทำให้ดูเหมือนไม่เจออะไรเลย)
    search() {
      this.$nextTick(() => {
        this.$refs.scrollContainer?.scrollTo({ top: 0 });
        this.updateActiveFromScroll();
      });
    },
  },
  methods: {
    soldOut(product) {
      return !product.is_in_stock || product.is_ingredient_insufficient;
    },
    // กดแล้วเปิดแผ่นตัวเลือกแทนที่จะหย่อนลงตะกร้าเลย — ปุ่มบนการ์ดใช้ค่านี้
    // เลือกไอคอน (sliders vs +) ให้ตรงกับสิ่งที่จะเกิดขึ้นจริง
    hasOptions(product) {
      return !!(product.has_variants || product.has_modifiers);
    },
    /**
     * รายละเอียดสินค้าสำหรับโชว์ใต้ชื่อเมนูบนการ์ด
     *
     * API ส่งเป็น HTML (sanitize แล้ว) และแผ่นรายละเอียดก็ render ตรง ๆ แต่บนการ์ด
     * ยุบเป็นข้อความล้วนก่อน เพราะช่องนี้คือ teaser 2 บรรทัดที่คุมด้วย -webkit-line-clamp
     * ซึ่งใช้กับลูกที่เป็น block (<p>/<ul>) ไม่ได้ — จะกลายเป็นการ์ดสูงไม่เท่ากันทั้งแถว
     *
     * ใช้ pickLocalized ตรง ๆ (ไม่ผ่าน $ln) เพราะ $ln ส่งค่าเข้า $t อีกชั้น ซึ่งเหมาะกับ
     * "ชื่อ" ที่อาจมีคีย์ในพจนานุกรม แต่ประโยคบรรยายไม่มีทางมีคีย์อยู่แล้ว
     */
    productDesc(product) {
      return stripHtml(pickLocalized(product, this.$i18n.locale, 'description'));
    },
    setSectionRef(id, el) {
      if (el) this.sectionEls[id] = el;
      else delete this.sectionEls[id];
    },
    setPillRef(id, el) {
      if (el) this.pillEls[id] = el;
      else delete this.pillEls[id];
    },
    onScroll() {
      if (this._rafPending) return;
      this._rafPending = true;
      requestAnimationFrame(() => {
        this._rafPending = false;
        this.updateActiveFromScroll();
      });
    },
    updateActiveFromScroll() {
      if (Date.now() < this._suppressSpyUntil) return;
      if (!this.visibleCategories.length) return;
      const container = this.$refs.scrollContainer;
      if (!container) return;

      const trigger = container.getBoundingClientRect().top + 8;
      let activeId = this.visibleCategories[0].id;

      for (const cat of this.visibleCategories) {
        const el = this.sectionEls[cat.id];
        if (!el) continue;
        const top = el.getBoundingClientRect().top;
        if (top <= trigger) {
          activeId = cat.id;
        } else {
          break;
        }
      }

      if (activeId !== this.activeCategoryId) {
        this.activeCategoryId = activeId;
      }
    },
    scrollToCategory(id, ev) {
      const el = this.sectionEls[id];
      const container = this.$refs.scrollContainer;
      if (!el || !container) return;
      ev?.currentTarget?.blur();

      this.activeCategoryId = id;
      this._suppressSpyUntil = Date.now() + 700;

      const containerRect = container.getBoundingClientRect();
      const elRect = el.getBoundingClientRect();
      const offset = container.scrollTop + (elRect.top - containerRect.top) - 4;
      container.scrollTo({ top: offset, behavior: 'smooth' });
    },
    scrollActivePillIntoView(id) {
      const pill = this.pillEls[id];
      const bar = this.$refs.pillBar;
      if (!pill || !bar) return;
      const pillRect = pill.getBoundingClientRect();
      const barRect = bar.getBoundingClientRect();
      const target = bar.scrollLeft
        + (pillRect.left - barRect.left)
        - (barRect.width / 2)
        + (pillRect.width / 2);
      bar.scrollTo({ left: target, behavior: 'smooth' });
    },
    /**
     * 422 "service session invalid!" means the session this menu is bound to
     * (token in the URL) is gone — closed/moved/stale. Send staff back to the
     * sessions list instead of leaving them on a dead menu. Returns true when
     * handled; guarded so repeated failures navigate only once.
     */
    bounceIfSessionInvalid(err) {
      if (!isServiceSessionInvalid(err)) return false;
      if (this._redirecting) return true;
      this._redirecting = true;
      // ปิด cart offcanvas ก่อน ไม่งั้น scroll lock/backdrop ของ Bootstrap
      // จะค้างไปหน้า sessions (ดู utils/offcanvasHelper)
      dismissOffcanvas('cartOffcanvas').then(() => {
        this.$router.replace({ name: 'pos.sessions' });
      });
      return true;
    },
    async fetchPlatformPrices() {
      const token = this.$route.params.token;
      this.platformInfo = useShopStore().getSessionPlatform(token);
      // ไม่มี state ในเครื่องนี้ (เปิดจากเครื่องอื่น/ล้าง storage) → ถาม session ตรง ๆ
      if (!this.platformInfo) {
        try {
          const res = await Sessions.getInfo(token);
          const data = res.data?.data ?? {};
          const dp = data.delivery_platform;
          if (dp?.is_external) {
            this.platformInfo = {
              delivery_platform_id: dp.id,
              external_order_ref: data.external_order_ref || '',
              name: dp.name,
              color: dp.color || '',
              is_external: true
            };
            useShopStore().setSessionPlatform(token, this.platformInfo);
          }
        } catch (err) { /* ปล่อยให้ flow ปกติทำงาน */ }
      }
      if (!this.platformInfo?.is_external) return;
      try {
        const res = await POSDeliveryPlatforms.prices(this.platformInfo.delivery_platform_id);
        this.platformPrices = res.data?.data?.prices ?? null;
      } catch (err) {
        this.platformPrices = null;
      }
    },
    // สินค้าที่มีราคา platform → ทับราคา + ปิดป้ายโปรในร้าน (โปรในร้านไม่ใช้กับออเดอร์ platform)
    applyPlatformPrices(products) {
      if (!this.platformPrices) return products;
      for (const p of products) {
        const override = this.platformPrices[p.id];
        if (override !== undefined && override !== null) {
          p.price = override;
          p.promotion_price = null;
        }
      }
      return products;
    },
    async fetchData() {
      this.loading = true;
      try {
        await this.fetchPlatformPrices();
        const res = await Sessions.products(this.$route.params.token);
        this.products = this.applyPlatformPrices(res.data?.data ?? []);
        this.categories = res.data?.filter?.categories ?? [];
        const first = this.visibleCategories[0];
        this.activeCategoryId = first ? first.id : null;
      } catch (err) {
        if (this.bounceIfSessionInvalid(err)) return;
        if (err?.response?.data?.message) {
          this.$toast.warn(this.$t(err?.response?.data?.message));
        }
      } finally {
        this.loading = false;
        this.pageLoading = false;
        this.$nextTick(() => this.updateActiveFromScroll());
      }
    },
    handleProductClick(product) {
      if (!product.is_in_stock || product.is_ingredient_insufficient) return;

      if (!product.has_variants && !product.has_modifiers) {
        this.addSimpleProduct(product);
      } else {
        this.offcanvasProduct.id = product.id;
        this.offcanvasProduct.data = product;
        this.offcanvasProduct.editData = null;
        this.offcanvasProduct.editIndex = -1;
        this.offcanvasProduct.open = true;
      }
    },
    addSimpleProduct(product) {
      this.$refs.cartPanel.addItem({
        product_id: product.id,
        _original_product_id: product.id,
        _variant_id: null,
        name: product.name,
        name_en: product.name_en,
        variant_name: null,
        variant_name_en: null,
        unit_price: parseFloat(product.price),
        quantity: 1,
        remark: null,
        modifier_option_ids: [],
        selected_modifiers: []
      });
    },
    onAddToCart(cartItem) {
      // Flatten modifier option ids for API payload
      const modifierOptionIds = [];
      if (cartItem.selected_modifiers) {
        for (const mod of cartItem.selected_modifiers) {
          for (const opt of mod.options) {
            modifierOptionIds.push(opt.id);
          }
        }
      }

      this.$refs.cartPanel.addItem({
        product_id: cartItem.variant_id || cartItem.product_id,
        _original_product_id: cartItem.product_id,
        _variant_id: cartItem.variant_id,
        name: cartItem.name,
        name_en: cartItem.name_en,
        variant_name: cartItem.variant_name,
        variant_name_en: cartItem.variant_name_en,
        unit_price: cartItem.unit_price,
        quantity: cartItem.quantity,
        remark: cartItem.remark || null,
        modifier_option_ids: modifierOptionIds,
        selected_modifiers: cartItem.selected_modifiers || []
      });

      this.offcanvasProduct.open = false;
    },
    handleEditCartItem({ index, item }) {
      this.offcanvasProduct.id = item._original_product_id || item.product_id;
      this.offcanvasProduct.editData = item;
      this.offcanvasProduct.editIndex = index;
      this.offcanvasProduct.editSentItem = null;
      this.offcanvasProduct.open = true;
    },
    handleEditSentItem(item) {
      const parentProductId = item.product_config || item.product_id;
      const variantId = item.product_config ? item.product_id : null;

      this.offcanvasProduct.id = parentProductId;
      this.offcanvasProduct.editIndex = -1;
      this.offcanvasProduct.editSentItem = item;
      this.offcanvasProduct.editData = {
        product_id: parentProductId,
        _variant_id: variantId,
        _attribute_options: Array.isArray(item.attribute_options) ? item.attribute_options : [],
        _modifier_option_ids: Array.isArray(item.modifiers)
          ? item.modifiers.map(m => m.modifier_option_id).filter(id => id != null)
          : [],
        quantity: parseInt(item.quantity) || 1,
        remark: item.remark || ''
      };
      this.offcanvasProduct.open = true;
    },
    async onUpdateCartItem({ index, ...cartItem }) {
      const modifierOptionIds = [];
      if (cartItem.selected_modifiers) {
        for (const mod of cartItem.selected_modifiers) {
          for (const opt of mod.options) {
            modifierOptionIds.push(opt.id);
          }
        }
      }

      const sentItem = this.offcanvasProduct.editSentItem;

      if (sentItem) {
        const payload = {
          product_id: cartItem.variant_id || cartItem.product_id,
          quantity: cartItem.quantity,
          remark: cartItem.remark || null,
          modifier_option_ids: modifierOptionIds
        };

        try {
          await Sessions.updateItem(this.$route.params.token, sentItem.id, payload);
          this.$toast.success(this.$t('Item updated successfully'));
          this.$refs.cartPanel.fetchData();
        } catch (err) {
          if (this.bounceIfSessionInvalid(err)) return;
          if (err?.response?.data?.message) {
            this.$toast.warn(this.$t(err?.response?.data?.message));
          }
          return;
        } finally {
          this.offcanvasProduct.open = false;
          this.offcanvasProduct.editData = null;
          this.offcanvasProduct.editIndex = -1;
          this.offcanvasProduct.editSentItem = null;
        }
        return;
      }

      this.$refs.cartPanel.updateItem(index, {
        product_id: cartItem.variant_id || cartItem.product_id,
        _original_product_id: cartItem.product_id,
        _variant_id: cartItem.variant_id,
        name: cartItem.name,
        name_en: cartItem.name_en,
        variant_name: cartItem.variant_name,
        variant_name_en: cartItem.variant_name_en,
        _unit_price: cartItem.unit_price,
        quantity: cartItem.quantity,
        remark: cartItem.remark || '',
        modifier_option_ids: modifierOptionIds,
        _selected_modifiers: cartItem.selected_modifiers || []
      });

      this.offcanvasProduct.open = false;
      this.offcanvasProduct.editData = null;
      this.offcanvasProduct.editIndex = -1;
    }
  },
  mounted() {
    this.fetchData();
  }
}
</script>

<style scoped>
.hide-scrollbar::-webkit-scrollbar { display: none; }
.hide-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }

/* On the order screen the layout pins this container to the viewport via
   POSLayout's .pos-fullheight flex chain, so it simply fills the height it's
   given and the inner .menu-scroll does all the scrolling. */
.pos-order-container { height: 100%; }

/*
  The product list's own scroll area. iOS momentum + overscroll containment
  keep it smooth and stop the scroll from chaining into the page/body (which
  felt like the list "couldn't reach" the top/bottom). On mobile the bottom
  padding clears the fixed "View Order" cart bar so the last row of cards is
  fully reachable; on desktop the bar is gone so only a little is needed.
  min-height:0 lets this flex child shrink so its own overflow scrolls instead
  of pushing the column taller.
*/
.menu-scroll {
  min-height: 0;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;
  padding-bottom: 0.5rem;
}
@media (max-width: 991.98px) {
  .menu-scroll {
    padding-bottom: calc(80px + env(safe-area-inset-bottom, 0px));
  }
}

.menu-section {
  scroll-margin-top: 8px;
}

/* --- ช่องค้นหาชื่ออาหาร ------------------------------------------------
   ทรงเดียวกับ pill ด้านล่าง (rounded-pill + เส้นบาง) ให้แถบหัวเมนูดูเป็นชุดเดียว */
.menu-search { position: relative; }
.menu-search__icon {
  position: absolute;
  left: 0.85rem;
  top: 50%;
  transform: translateY(-50%);
  color: #adb5bd;
  font-size: 0.85rem;
  pointer-events: none;
}
.menu-search__input {
  width: 100%;
  padding: 0.45rem 2.3rem;
  font-size: 0.9rem;
  line-height: 1.4;
  color: #333;
  background: #f8f9fa;
  border: 1px solid #e7e7e7;
  border-radius: 999px;
  transition: background-color 0.18s ease, border-color 0.18s ease, box-shadow 0.18s ease;
}
.menu-search__input::placeholder { color: #adb5bd; }
.menu-search__input:focus {
  outline: none;
  background: #fff;
  border-color: #e96c15;
  box-shadow: 0 0 0 3px rgba(233, 108, 21, 0.15);
}
.menu-search__clear {
  position: absolute;
  right: 0.4rem;
  top: 50%;
  transform: translateY(-50%);
  width: 26px;
  height: 26px;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.06);
  color: #6c757d;
  font-size: 0.75rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.menu-search__clear:hover { background: rgba(0, 0, 0, 0.12); }

/*
  Compact category pills (shared design with the customer menu + preview).
  Base size is tuned for mobile; on lg+ (the POS terminal) they grow a
  notch so they stay comfortable on a big screen. Custom button avoids the
  sticky touch :hover/:focus fill Bootstrap's outline buttons used to leave.
*/
.cat-pill {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.3rem 0.7rem;
  font-size: 0.8rem;
  font-weight: 600;
  line-height: 1.4;
  white-space: nowrap;
  color: #6c757d;
  background: #fff;
  border: 1px solid #e7e7e7;
  border-radius: 999px;
  cursor: pointer;
  transition: color 0.18s ease, background-color 0.18s ease,
              border-color 0.18s ease, box-shadow 0.18s ease;
}
.cat-pill:focus,
.cat-pill:focus-visible { outline: none; box-shadow: none; }
.cat-pill.active {
  color: #fff;
  background: #e96c15;
  border-color: #e96c15;
  box-shadow: 0 3px 10px rgba(233, 108, 21, 0.3);
}
.cat-pill-count {
  font-size: 0.68rem;
  font-weight: 700;
  min-width: 1.15rem;
  padding: 0 0.3rem;
  text-align: center;
  border-radius: 999px;
  background: rgba(0, 0, 0, 0.06);
  color: inherit;
}
.cat-pill.active .cat-pill-count { background: rgba(255, 255, 255, 0.25); }
@media (min-width: 992px) {
  .cat-pill { padding: 0.45rem 1rem; font-size: 0.9rem; gap: 0.45rem; }
  .cat-pill-count { font-size: 0.72rem; min-width: 1.3rem; }
}

/* Slim section header: theme accent bar + small heading + muted count. */
.section-accent {
  display: inline-block;
  width: 3px;
  height: 1rem;
  border-radius: 999px;
  background: #e96c15;
  margin-right: 0.55rem;
}
.section-title { font-size: 1rem; letter-spacing: 0.1px; }
.section-count { font-size: 0.72rem; font-weight: 700; color: #adb5bd; }
@media (min-width: 992px) {
  .section-title { font-size: 1.1rem; }
  .section-accent { height: 1.15rem; }
}

/* --- Poster product card (เมนูแนะนำ) ---------------------------------
   รูปเต็มกรอบด้านบน + แถบพื้นขาวใต้รูปสำหรับชื่อ/รายละเอียด/ปุ่มเพิ่ม
   (เดิมชื่อวางทับรูปบน gradient — อ่านยากเมื่อรูปสว่างและไม่มีที่ให้คำบรรยาย)
   ป้ายราคา/ตัวเลือก/ของหมด ยังลอยอยู่บนรูปเหมือนเดิม */
.product-card {
  display: flex;
  flex-direction: column;
  border-radius: 16px;
  overflow: hidden;
  cursor: pointer;
  background: #fff;
  border: 1px solid #f0f0f0;
  transition: transform 0.25s ease, box-shadow 0.25s ease;
}
.product-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 14px 26px rgba(0, 0, 0, 0.16) !important;
}
.product-card--disabled { opacity: 0.55; }
.product-media {
  position: relative;
  aspect-ratio: 1 / 1;            /* square poster */
  overflow: hidden;
  background: #1d1d1d;            /* fallback shows through if image fails */
}
.product-image {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.5s ease;
}
.product-card:hover .product-image { transform: scale(1.06); }
.product-placeholder {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #e96c15 0%, #ff8a3c 100%);
  color: rgba(255, 255, 255, 0.7);
  font-size: 2.5rem;
}
/* แถบพื้นขาว: ข้อความชิดซ้าย ปุ่มเพิ่มชิดขวาล่าง */
.product-body {
  flex-grow: 1;
  display: flex;
  align-items: flex-end;
  gap: 0.5rem;
  padding: 0.5rem 0.6rem 0.6rem;
  background: #fff;
}
.product-body__text {
  flex-grow: 1;
  min-width: 0;
}
.product-name {
  font-size: 0.85rem;
  font-weight: 700;
  color: #212529;
  /* 1.8 (ไม่ใช่ 1.25) กันวรรณยุกต์บน (ก๋, เล้ง) และสระล่าง (ตุ๋น) ถูก
     กล่อง line-clamp (overflow:hidden) ตัดหัว/ตัดหาง */
  line-height: 1.8;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  overflow-wrap: anywhere;
}
.product-desc {
  margin-top: 0.1rem;
  font-size: 0.72rem;
  line-height: 1.65;
  color: #8a8f98;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  overflow-wrap: anywhere;
}
@media (min-width: 992px) {
  .product-name { font-size: 0.92rem; }
  .product-desc { font-size: 0.76rem; }
}

/* --- การ์ดแนวนอน (หมวดทั่วไป) ---------------------------------------
   รูปเล็กซ้าย + ชื่อ/รายละเอียด/ราคา ขวา — สแกนรายการยาว ๆ ได้เร็วกว่าตาราง
   โปสเตอร์ และมีที่ว่างพอให้คำบรรยายเมนูจริง ๆ */
.product-row {
  height: 100%;
  display: flex;
  gap: 0.7rem;
  padding: 0.55rem;
  cursor: pointer;
  background: #fff;
  border: 1px solid #f0f0f0;
  border-radius: 14px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;
}
.product-row:hover {
  transform: translateY(-2px);
  border-color: rgba(233, 108, 21, 0.35);
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
}
.product-row--disabled { opacity: 0.6; }
.product-row__media {
  position: relative;
  flex-shrink: 0;
  width: 88px;
  height: 88px;
  border-radius: 11px;
  overflow: hidden;
  background: #1d1d1d;
}
.product-row__image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.product-row__placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #e96c15 0%, #ff8a3c 100%);
  color: rgba(255, 255, 255, 0.7);
  font-size: 1.5rem;
}
/* ของหมด: แถบคาดล่างรูป (ไม่ใช่ป้ายกลาง) — รูปเล็ก ป้ายกลางจะบังทั้งใบ */
.product-row__stock {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  padding: 0.12rem 0;
  text-align: center;
  background: rgba(0, 0, 0, 0.78);
  color: #fff;
  font-size: 0.62rem;
  font-weight: 700;
}
.product-row__body {
  flex-grow: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}
.product-row__foot {
  margin-top: auto;          /* ราคา/ปุ่ม ยึดขอบล่างเสมอ ไม่ว่าคำบรรยายจะกี่บรรทัด */
  padding-top: 0.35rem;
  display: flex;
  /* บนแท็บเล็ต (lg + cart panel เปิด) การ์ดเหลือ ~230px ที่ว่างข้างรูปจึงสั้นมาก
     wrap + margin-left:auto ทำให้ราคาที่ยาว (ราคาโปรฯ + ราคาเดิม) ดันปุ่มตก
     บรรทัดใหม่แทนที่จะล้นออกนอกกรอบการ์ด */
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 0.35rem 0.5rem;
}
.product-row__foot .card-add-btn { margin-left: auto; }
.row-price {
  display: inline-flex;
  flex-wrap: wrap;           /* ราคาโปรฯ + ราคาเดิม ตกบรรทัดกันได้ */
  align-items: baseline;
  column-gap: 0.3rem;
  font-size: 0.92rem;
  font-weight: 800;
  color: #e96c15;
  white-space: nowrap;
}
.row-price--promo { color: #16a34a; }
.row-price__old {
  font-size: 0.72rem;
  font-weight: 500;
  color: #adb5bd;
}
/* ขยายรูปเฉพาะจอที่การ์ดกว้างจริง — ที่ lg การ์ดแคบสุด (cart panel เปิด)
   รูปใหญ่จะไปเบียดชื่อเมนูจนตกบรรทัด */
@media (min-width: 1400px) {
  .product-row__media { width: 100px; height: 100px; }
}

.overlay-badges {
  position: absolute;
  top: 0.5rem;
  left: 0.5rem;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.3rem;
  z-index: 2;
  max-width: calc(100% - 1rem);
}
.overlay-price {
  display: inline-flex;
  align-items: center;
  background: #e96c15;
  color: #fff;
  font-weight: 800;
  padding: 0.22rem 0.6rem;
  border-radius: 999px;
  font-size: 0.76rem;
  box-shadow: 0 3px 8px rgba(0, 0, 0, 0.25);
  white-space: nowrap;
  max-width: 100%;
}
.overlay-price--reco { background: #ffc107; color: #1d1d1d; }
.overlay-price--promo { background: #16a34a; }
.overlay-options {
  display: inline-flex;
  align-items: center;
  background: rgba(255, 255, 255, 0.95);
  color: #d46010;
  border: 1px solid rgba(233, 108, 21, 0.25);
  font-weight: 600;
  padding: 0.18rem 0.5rem;
  border-radius: 999px;
  font-size: 0.66rem;
  box-shadow: 0 3px 8px rgba(0, 0, 0, 0.2);
  backdrop-filter: blur(4px);
  white-space: nowrap;
  max-width: 100%;
  overflow: hidden;
}
/* Centred over the (dimmed) image so it never collides with the price /
   options chips in the top-left corner, no matter how wide the price is. */
.overlay-stock {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 3;
  background: rgba(0, 0, 0, 0.82);
  color: #fff;
  padding: 0.3rem 0.85rem;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 700;
  white-space: nowrap;
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.35);
}
.card-add-btn {
  width: 30px;
  height: 30px;
  padding: 0;
  border: none;
  border-radius: 999px;
  background: #e96c15;
  color: #fff;
  font-size: 0.8rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 3px 8px rgba(0, 0, 0, 0.3);
  transition: transform 0.12s ease, background 0.15s ease;
}
.card-add-btn:hover:not(:disabled) { background: #d46010; }
.card-add-btn:active:not(:disabled) { transform: scale(0.9); }
/* มีตัวเลือก (variant/modifier) = กดแล้วเปิดแผ่นตัวเลือก ไม่ใช่หย่อนลงตะกร้าเลย
   จึงใช้ปุ่มโปร่งไอคอน sliders แยกจากปุ่ม + ทึบที่เพิ่มของเข้าตะกร้าทันที
   (แทนชิป "ตัวเลือก" เดิมที่กินที่จนล้นกรอบการ์ดบนแท็บเล็ต) */
.card-add-btn--options {
  background: #fff;
  color: #e96c15;
  border: 1.5px solid rgba(233, 108, 21, 0.55);
  box-shadow: 0 2px 6px rgba(233, 108, 21, 0.18);
}
.card-add-btn--options:hover:not(:disabled) { background: #fff3e9; }
/* ปุ่มอยู่บนพื้นขาวแล้ว (ไม่ใช่ทับรูป) — disabled จึงเป็นเทาให้เห็นว่ากดไม่ได้ */
.card-add-btn--options:disabled { border-color: #dee2e6; color: #adb5bd; }
.card-add-btn:disabled {
  background: #dee2e6;
  box-shadow: none;
  cursor: not-allowed;
}

/* Loading placeholder shaped like the square poster card. */
.skeleton-poster {
  aspect-ratio: 1 / 1;
  min-height: 150px;
  border-radius: 16px;
}

@media (max-width: 380px) {
  .product-name { font-size: 0.78rem; }
  .product-desc { font-size: 0.68rem; }
  .overlay-price { font-size: 0.68rem; padding: 0.2rem 0.5rem; }
  .overlay-options { font-size: 0.6rem; }
  .product-row__media { width: 76px; height: 76px; }
}
</style>

